import React from 'react';
import { CalendarIcon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { COMMUNITY_EVENTS, CommunityEvent } from '../data/community-events';
function EventCard({
  event,
  isEs
}: {
  event: CommunityEvent;
  isEs: boolean;
}) {
  const title = isEs && event.titleEs ? event.titleEs : event.title;
  const date = isEs && event.dateEs ? event.dateEs : event.date;
  const content = <>
      <div className="w-full h-40 overflow-hidden bg-haa-navy/5" data-id="element-3561" data-mp="27nqh4r6xeza7">
        <img src={event.image} alt={title} loading="lazy" className="w-full h-full object-cover object-top" data-id="element-3562" data-mp="2cmjwyq9or2dj" />
      </div>
      <div className="px-4 py-3.5" data-id="element-3563" data-mp="2lo195xjz2jwy">
        <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em] text-haa-blue mb-1.5 truncate" data-id="element-3564" data-mp="2loaydix70n1l">
          {event.organization}
        </p>
        <h3 className="font-bebas text-lg tracking-[0.03em] text-haa-navy leading-tight mb-2 line-clamp-2" data-id="element-3565" data-mp="narfe4hnbszq">
          {title}
        </h3>
        <p className="flex items-center gap-1.5 font-nunito text-xs font-bold text-haa-navy/70 mb-1" data-id="element-3566" data-mp="1esb4g825g7y5">
          <CalendarIcon className="w-3 h-3 flex-shrink-0 text-haa-blue" data-id="element-3567" data-mp="1a0b6gl5n3p47" />
          {date}
        </p>
        <p className="flex items-center gap-1.5 font-nunito text-xs text-haa-navy/55" data-id="element-3568" data-mp="153azm80yuic1">
          <MapPinIcon className="w-3 h-3 flex-shrink-0 text-red-600" data-id="element-3569" data-mp="103y3ewo7fyws" />
          {event.city}
        </p>
      </div>
    </>;
  const shell = 'flex-shrink-0 w-64 mx-3 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden';
  if (!event.url) {
    return <div className={shell} data-id="element-3570" data-mp="1nsr1ayoe0gu1">{content}</div>;
  }
  const isExternal = event.url.startsWith('http');
  return <a href={event.url} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined} className={`${shell} block transition-shadow duration-200 hover:shadow-lg`} aria-label={`${title} — ${event.city}, ${date}`} data-id="element-3571" data-mp="1itwj9yy8uwaz">
      {content}
    </a>;
}

/**
 * Continuously scrolling band of upcoming community events.
 * Pauses on hover/focus so a visitor can read and click a card, and
 * respects prefers-reduced-motion via the shared marquee utility.
 */
export function UpcomingEventsScroller() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  if (COMMUNITY_EVENTS.length === 0) return null;
  const doubled = [...COMMUNITY_EVENTS, ...COMMUNITY_EVENTS];
  return <section className="bg-white border-y border-gray-200 py-8 overflow-hidden" aria-label={isEs ? 'Próximos eventos' : 'Upcoming events'} data-id="element-3572" data-mp="2bv5t6qsptia6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-5" data-id="element-3573" data-mp="22wk2g8d3awyb">
        <span className="font-nunito text-haa-blue text-xs uppercase tracking-[0.3em] font-semibold block mb-1" data-id="element-3574" data-mp="27uvcaj3dx7gj">
          {isEs ? 'Calendario Comunitario' : 'Community Calendar'}
        </span>
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-[0.03em] text-haa-navy leading-tight" data-id="element-3575" data-mp="1szxl3tjz2o11">
          {isEs ? 'Próximos Eventos' : 'Upcoming Events'}
        </h2>
      </div>

      <div className="group relative w-full overflow-hidden" data-id="element-3576" data-mp="1ecyu1zp11vmu">
        <div className="flex w-max animate-events-marquee group-hover:[animation-play-state:paused]" data-id="element-3577" data-mp="1aac98iqrxaz5">
          {doubled.map((event, i) => <EventCard key={`${event.title}-${i}`} event={event} isEs={isEs} data-id="element-3578" data-mp="1o6pfswtrrryu" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}